import { computeSavedEntityState } from 'state/utils/computeSavedEntityState';
import { dashboard } from 'types';
import { toJSON, toJSONArray } from '@tock/coretypes';
import { todayBusinessDay } from '@tock/shared/utils/time';
import AppConstants from 'constants/AppConstants';
import _ from 'lodash';

export type PacingSource = 'GLOBAL' | 'INVENTORY_SCHEDULE';

interface PacingSavedState {
  applyPacingLimits: boolean;
  businessPacingDay: dashboard.BusinessPacingDayLimit[];
  businessPacingDayGlobal?: dashboard.BusinessPacingDayLimit;
  businessPacingDayByTime: Dictionary<dashboard.BusinessPacingDayLimit>;
  pacingSource: PacingSource;
}

interface SavedState {
  [date: string]: PacingSavedState;
}

export interface PacingState {
  applyPacingLimits: boolean;
  businessPacing: dashboard.BusinessPacingLimit;
  businessPacingWeekday: dashboard.BusinessPacingWeekday[];
  businessPacingByWeekdayTime: Dictionary<Dictionary<dashboard.BusinessPacingWeekday>>;
  businessPacingDay: dashboard.BusinessPacingDayLimit[];
  businessPacingDayGlobal?: dashboard.BusinessPacingDayLimit;
  businessPacingDayByTime: Dictionary<dashboard.BusinessPacingDayLimit>;
  pacingSource: PacingSource;
  savedState: SavedState;
}

const defaultBusinessPacing: dashboard.BusinessPacingLimit = {
  id: 0,
  businessId: 0,
  applyPacingLimits: false,
  maxTotalCovers: Infinity,
  maxOnlineCovers: Infinity,
};

const initialState: PacingState = {
  applyPacingLimits: false,
  businessPacing: defaultBusinessPacing,
  businessPacingWeekday: [],
  businessPacingByWeekdayTime: {},
  businessPacingDay: [],
  businessPacingDayGlobal: undefined,
  businessPacingDayByTime: {},
  pacingSource: 'GLOBAL',
  savedState: {},
};

function hasLimitsFromInventorySchedule(
  businessPacingDayLimits: dashboard.BusinessPacingDayLimit[]
): boolean {
  return businessPacingDayLimits.some((pacingDayLimit) => pacingDayLimit.fromInventorySchedule);
}

export default function reducer(
  state: PacingState = initialState,
  action: BaseTockAction
): PacingState {
  const { type, payload } = action;
  switch (type) {
    case AppConstants.BUSINESS_PACING_FETCH_DONE: {
      const businessPacing = toJSON<dashboard.BusinessPacingLimit>(payload);
      return {
        ...state,
        applyPacingLimits:
          businessPacing.applyPacingLimits ||
          hasLimitsFromInventorySchedule(state.businessPacingDay),
        businessPacing: _.isEmpty(businessPacing) ? defaultBusinessPacing : businessPacing,
      };
    }
    case AppConstants.BUSINESS_PACING_WEEKDAY_FETCH_DONE: {
      const businessPacingByWeekdayTime: Dictionary<Dictionary<dashboard.BusinessPacingWeekday>> =
        {};
      const businessPacingWeekday = toJSONArray<dashboard.BusinessPacingWeekday>(payload);

      _.forEach(businessPacingWeekday, (pacing) => {
        businessPacingByWeekdayTime[pacing.weekday] =
          businessPacingByWeekdayTime[pacing.weekday] || {};
        businessPacingByWeekdayTime[pacing.weekday][pacing.startTime] = pacing;
      });

      return {
        ...state,
        businessPacingWeekday,
        businessPacingByWeekdayTime,
      };
    }
    case AppConstants.BUSINESS_DAY_PACING_FETCH_DONE: {
      const businessPacingDayByTime: Dictionary<dashboard.BusinessPacingDayLimit> = {};
      const businessPacingDay = toJSONArray<dashboard.BusinessPacingDayLimit>(payload);
      const {
        context: { serviceLevelCapacitySettingsEnabled },
      } = action;
      let businessPacingDayGlobal!: dashboard.BusinessPacingDayLimit;
      _.forEach(businessPacingDay, (pacing) => {
        if (pacing.startTime) {
          businessPacingDayByTime[pacing.startTime] = pacing;
        } else {
          businessPacingDayGlobal = pacing;
        }
      });
      const isInventorySchedulePacing = hasLimitsFromInventorySchedule(businessPacingDay);

      return {
        ...state,
        applyPacingLimits: !serviceLevelCapacitySettingsEnabled
          ? state.businessPacing.applyPacingLimits
          : isInventorySchedulePacing,
        businessPacingDay,
        businessPacingDayByTime,
        businessPacingDayGlobal,
        pacingSource: isInventorySchedulePacing ? 'INVENTORY_SCHEDULE' : 'GLOBAL',
      };
    }
    case AppConstants.CALENDAR_ENTITY_SAVE_DATE: {
      const stateToSave: PacingSavedState = {
        applyPacingLimits: state.applyPacingLimits,
        businessPacingDay: state.businessPacingDay,
        businessPacingDayByTime: state.businessPacingDayByTime,
        businessPacingDayGlobal: state.businessPacingDayGlobal,
        pacingSource: state.pacingSource,
      };
      const newSavedState: SavedState = computeSavedEntityState<PacingSavedState>({
        newDate: payload,
        stateToSave,
        savedState: state.savedState,
        todayString: todayBusinessDay().toString(),
      });

      return {
        ...state,
        savedState: newSavedState,
      };
    }
    case AppConstants.CALENDAR_ENTITY_RESTORE_DATE: {
      const restoredState = state.savedState[payload];
      if (!restoredState) {
        return {
          ...state,
          applyPacingLimits: initialState.applyPacingLimits,
          businessPacingDay: initialState.businessPacingDay,
          businessPacingDayByTime: initialState.businessPacingDayByTime,
          businessPacingDayGlobal: initialState.businessPacingDayGlobal,
          pacingSource: initialState.pacingSource,
        };
      }
      return {
        ...state,
        applyPacingLimits: restoredState.applyPacingLimits,
        businessPacingDay: restoredState.businessPacingDay,
        businessPacingDayByTime: restoredState.businessPacingDayByTime,
        businessPacingDayGlobal: restoredState.businessPacingDayGlobal,
        pacingSource: restoredState.pacingSource,
      };
    }

    case AppConstants.BUSINESS_DAY_PACING_FETCH_FAILED: {
      return {
        ...state,
      };
    }
    case AppConstants.BUSINESS_PACING_WEEKDAY_FETCH: {
      return {
        ...state,
        businessPacingWeekday: [],
      };
    }
    case AppConstants.BUSINESS_PACING_FETCH_FAILED: {
      return {
        ...state,
        businessPacing: defaultBusinessPacing,
      };
    }
    default:
      return state;
  }
}
